/**
 * MarkdownContent — renders a message body written in markdown.
 *
 * Kim's drafts are markdown (the extension's "Edit & send to review" editor
 * serializes TipTap to markdown before sending), so rendering them as plain
 * text showed reviewers literal `**bold**` and `- item` syntax.
 *
 * `marked` passes raw HTML through, which is what we want for agent replies
 * pulled from a helpdesk — those are often HTML rather than markdown. Every
 * result goes through DOMPurify before it reaches `dangerouslySetInnerHTML`,
 * since message bodies are attacker-influenced (a customer can put anything in
 * an email).
 *
 * `breaks: true` matches the extension's editor config and preserves the
 * single-newline behaviour the previous `whitespace-pre-wrap` rendering had —
 * without it, soft line breaks inside a paragraph collapse into one line.
 */

import { useMemo } from "react";
import { marked } from "marked";
import { sanitizeHtmlWithNewTabLinks } from "@/lib/sanitize";
import { cn } from "@/lib/utils";
import { MARKDOWN_PROSE } from "@/components/markdown-prose";

interface MarkdownContentProps {
  /** Raw message body. Renders nothing when empty. */
  text: string | null | undefined;
  /** Typography and colour for the block — e.g. `"text-xs text-foreground"`. */
  className?: string;
}

export function MarkdownContent({ text, className }: MarkdownContentProps) {
  const html = useMemo(() => {
    if (!text?.trim()) return "";
    return sanitizeHtmlWithNewTabLinks(marked(text, { async: false, breaks: true, gfm: true }));
  }, [text]);

  if (!html) return null;

  return (
    <div
      className={cn(MARKDOWN_PROSE, className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
